import type { ReactNode } from 'react';
import { IMPACT_META, type ImpactLevel } from '@/mocks/marketCalendar';

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="max-w-2xl">
      {eyebrow && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          {eyebrow}
        </span>
      )}
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        {title}
      </h2>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-slate-500 md:text-base">{subtitle}</p>}
    </div>
  );
}

export function StatCard({
  label,
  value,
  sub,
  icon,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: string;
  tone?: 'neutral' | 'positive' | 'accent' | 'warn';
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'accent'
        ? 'text-violet-700'
        : tone === 'warn'
          ? 'text-amber-600'
          : 'text-slate-900';
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {icon && <i className={`${icon} text-base text-slate-300`} />}
      </div>
      <p className={`mt-1.5 font-mono text-xl font-bold tabular-nums ${toneClass}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

export function ImpactDots({ impact, size = 'sm' }: { impact: ImpactLevel; size?: 'sm' | 'lg' }) {
  const meta = IMPACT_META[impact];
  const dot = size === 'lg' ? 'h-2 w-2' : 'h-1.5 w-1.5';
  return (
    <span className="inline-flex items-center gap-0.5" title={`Tác động ${meta.label}`}>
      {Array.from({ length: 3 }).map((_, i) => (
        <span
          key={i}
          className={`rounded-full ${dot}`}
          style={{ backgroundColor: i < meta.dots ? meta.color : '#e2e8f0' }}
        />
      ))}
    </span>
  );
}

export function ImpactBadge({ impact }: { impact: ImpactLevel }) {
  const meta = IMPACT_META[impact];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.bg} ${meta.text}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

export function CurrencyFlag({ code }: { code: string }) {
  const map: Record<string, string> = {
    USD: '🇺🇸',
    EUR: '🇪🇺',
    GBP: '🇬🇧',
    JPY: '🇯🇵',
    AUD: '🇦🇺',
    CAD: '🇨🇦',
    CHF: '🇨🇭',
    NZD: '🇳🇿',
    XAU: '🥇',
    CNY: '🇨🇳',
  };
  return <span className="text-base leading-none">{map[code] ?? '🏳️'}</span>;
}

export function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
        active
          ? 'border-violet-600 bg-violet-600 text-white'
          : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700'
      }`}
    >
      {children}
    </button>
  );
}